import * as React from 'react';
import { Component } from 'react';
import * as Moment from 'moment';
import { History, Location } from 'history';
import { getBasket } from '../../../services/public/basket';
import { AppOneTimeCode } from '../../AppOrders/AppOrders';
import { updateBasketItemQuantity } from '../../../services/public/basket';
import { VoucherForm } from '../CommonComponents/VoucherForm/VoucherForm';
import { SimpleModal } from '../../../components/SimpleModal/SimpleModal';
import { getPublicVoucher } from '../../../services/public/vouchers';
import { CURRENCY_SYMBOL } from '../../../consts/common';
import { Button } from 'Src/components/Button/Button';
import './Basket.scss';
import * as propz from 'propz';

interface Props {
  oneTimeCode: AppOneTimeCode;
  history: History;
  location: Location;
  setBasketCount: (count: number) => void;
  allowCustomerAccess: boolean;
}

interface State {
  galleryImages: any[];
  basketItems: any[];
  orderAmount: number;
  deliveryAmount: number;
  isVoucherModalOpen: boolean;
  voucherText: string;
  isVoucherEnterButtonClicked: boolean;
  voucherSaving: number;
  specialOffersSaving: number;
  totalWithoutSpecialOffers: number;
  isAutoVoucherModalOpen: boolean;
  autoVoucherMessage: string;
  voucher: any;
}

export class Basket extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      galleryImages: [],
      basketItems: [],
      orderAmount: 0.0,
      deliveryAmount: 0.0,
      isVoucherModalOpen: false,
      voucherText: '',
      isVoucherEnterButtonClicked: false,
      voucherSaving: 0.0,
      specialOffersSaving: 0.0,
      totalWithoutSpecialOffers: 0.0,
      isAutoVoucherModalOpen: false,
      autoVoucherMessage: '',
      voucher: null,
    };
  }

  private formatVoucherSaving(voucherSaving: number, voucher: any): string {
    const discountType = voucher.discount?.discountType;
    const discountAmount = voucher.discount?.discountAmount ?? 0;
    const priceType = voucher.discount?.priceType;

    if (discountType === 'REDUCED_POSTAGE') {
      if (priceType === 'AMOUNT') {
        return `Standard delivery discount: ${CURRENCY_SYMBOL.POUND}${discountAmount.toFixed(2)}`;
      }
      if (priceType === 'PERCENT') {
        return `Standard delivery discount: ${discountAmount.toFixed(0)}%`;
      }
      return `Standard delivery discount: ${CURRENCY_SYMBOL.POUND}${discountAmount.toFixed(2)}`;
    }

    if (discountType === 'FREE_POSTAGE') {
      return 'Free standard delivery';
    }

    const parts: string[] = [];
    if (voucherSaving > 0) {
      parts.push(`Your savings: ${CURRENCY_SYMBOL.POUND}${voucherSaving.toFixed(2)}`);
    }
    if (voucher.isFreePostage) {
      parts.push('Free standard delivery');
    }
    return parts.join(' and ');
  }

  componentDidMount() {
    const { oneTimeCode, history, setBasketCount, allowCustomerAccess } = this.props;
    const { isAuthQuestionAsked } = oneTimeCode;

    if (allowCustomerAccess && !isAuthQuestionAsked) {
      history.push('/oneTimeCodeReady');
    }

    getBasket(oneTimeCode).then(basket => {
      const {
        items,
        orderAmount,
        deliveryAmount = 0,
        galleryImages,
        specialOffersSaving,
        totalWithoutSpecialOffers,
        voucherSaving,
        voucher,
      } = basket;

      const formattedVoucherSaving = this.formatVoucherSaving(voucherSaving, voucher);

      this.setState({
        galleryImages,
        basketItems: items,
        orderAmount: orderAmount.toFixed(2),
        deliveryAmount: deliveryAmount.toFixed(2),
        specialOffersSaving: specialOffersSaving.toFixed(2),
        totalWithoutSpecialOffers: totalWithoutSpecialOffers.toFixed(2),
        voucher: basket.voucher,
        voucherSaving: voucherSaving.toFixed(2),
        isAutoVoucherModalOpen: Boolean(voucher?.voucherId && voucher.autoApply),
        autoVoucherMessage:
          voucher?.voucherId && voucher.autoApply
            ? `Voucher "${voucher.code}" was automatically applied! Your savings: ${formattedVoucherSaving}.`
            : '',
      });
      setBasketCount(basket.items.length);
    });
  }

  onPlusClick = (item: any, index: number) => {
    const { oneTimeCode } = this.props;
    const { imageId, productId, quantity, offerId } = item;
    const nextQuantity = quantity + 1;

    updateBasketItemQuantity(oneTimeCode, {
      imageId,
      productId,
      quantity: nextQuantity,
      offerId,
    })
      .then(() => getBasket(oneTimeCode))
      .then(basket => {
        const { orderAmount, specialOffersSaving, totalWithoutSpecialOffers, voucherSaving, voucher } = basket;
        const basketItems = [...this.state.basketItems];
        basketItems[index].quantity = nextQuantity;

        const formattedVoucherSaving = this.formatVoucherSaving(voucherSaving, voucher);

        this.setState({
          basketItems,
          orderAmount: orderAmount.toFixed(2),
          specialOffersSaving: specialOffersSaving.toFixed(2),
          totalWithoutSpecialOffers: totalWithoutSpecialOffers.toFixed(2),
          voucher: basket.voucher,
          voucherSaving: voucherSaving.toFixed(2),
          isAutoVoucherModalOpen: Boolean(voucher?.voucherId && voucher.autoApply),
          autoVoucherMessage:
            voucher?.voucherId && voucher.autoApply
              ? `Voucher "${voucher.code}" was automatically applied! Your savings: ${formattedVoucherSaving}.`
              : '',
        });
      });
  };

  onMinusClick = (item: any, index: number) => {
    const { oneTimeCode, setBasketCount } = this.props;
    const { imageId, productId, quantity, offerId } = item;
    const nextQuantity = quantity - 1;

    if (nextQuantity >= 0) {
      updateBasketItemQuantity(oneTimeCode, {
        imageId,
        productId,
        quantity: nextQuantity,
        offerId,
      })
        .then(() => getBasket(oneTimeCode))
        .then(basket => {
          const { orderAmount, specialOffersSaving, totalWithoutSpecialOffers, voucherSaving, voucher } = basket;
          const basketItems = [...this.state.basketItems];
          basketItems[index].quantity = nextQuantity;
          const basketItemsFiltered = basketItems.filter(item => item.quantity !== 0);

          const formattedVoucherSaving = this.formatVoucherSaving(voucherSaving, voucher);

          this.setState({
            basketItems: basketItemsFiltered,
            orderAmount: orderAmount.toFixed(2),
            specialOffersSaving: specialOffersSaving.toFixed(2),
            totalWithoutSpecialOffers: totalWithoutSpecialOffers.toFixed(2),
            voucher: basket.voucher,
            voucherSaving: voucherSaving.toFixed(2),
            isAutoVoucherModalOpen: Boolean(voucher?.voucherId && voucher.autoApply),
            autoVoucherMessage:
              voucher?.voucherId && voucher.autoApply
                ? `Voucher "${voucher.code}" was automatically applied! Your savings: ${formattedVoucherSaving}.`
                : '',
          });
          setBasketCount(basketItemsFiltered.length);
        });
    }
  };

  onVoucherClick = () => {
    this.setState({
      isVoucherModalOpen: true,
    });
  };

  onGalleryButtonClick = () => {
    this.props.history.push('/photoGallery');
  };

  onCheckoutButtonClick = () => {
    const { basketItems } = this.state;

    const isSchoolDeliveryAvailable = basketItems.some(item => {
      return item.isSchoolDeliveryAvailable;
    });

    if (isSchoolDeliveryAvailable) {
      this.props.history.push('/deliveryOptions');
    } else {
      this.props.history.push('/homeDelivery');
    }
  };

  getYear = (date: Date) => {
    return Moment(date).format('YYYY');
  };

  onClearBasketClick = () => {
    const { oneTimeCode, setBasketCount } = this.props;
    const { basketItems } = this.state;

    const clearPromises = basketItems.map(item => {
      const { imageId, productId, offerId } = item;

      return updateBasketItemQuantity(oneTimeCode, {
        imageId,
        productId,
        quantity: 0,
        offerId,
      });
    });

    Promise.all(clearPromises)
      .then(() => {
        this.setState({
          basketItems: [],
          orderAmount: 0.0,
          deliveryAmount: 0.0,
          specialOffersSaving: 0.0,
          totalWithoutSpecialOffers: 0.0,
          voucherSaving: 0.0,
        });
        setBasketCount(0);
      })
      .catch(error => {
        console.error('Error clearing basket items', error);
      });
  };

  renderVoucherModal = () => {
    const { isVoucherModalOpen, voucherText, isVoucherEnterButtonClicked } = this.state;
    return (
      <SimpleModal isOpen={isVoucherModalOpen}>
        <VoucherForm
          onCancel={this.onVoucherCancelClick}
          onSubmit={this.onVoucherEnterClick}
          onOk={this.onVoucherOkClick}
          text={voucherText}
          isEnterButtonClicked={isVoucherEnterButtonClicked}
          {...this.props}
        />
      </SimpleModal>
    );
  };

  onVoucherEnterClick = (data: any) => {
    const { oneTimeCode } = this.props;
    const { code } = data;

    // 1) apply the voucher
    getPublicVoucher(oneTimeCode, code)
      // 2) then re‑pull the entire basket
      .then(() => getBasket(oneTimeCode))
      // 3) and now update *all* the state fields from the basket
      .then(basket => {
        const {
          items,
          galleryImages,
          orderAmount,
          deliveryAmount = 0,
          specialOffersSaving,
          totalWithoutSpecialOffers,
          voucherSaving,
          voucher,
        } = basket;

        const formattedVoucherSaving = this.formatVoucherSaving(voucherSaving, voucher);

        this.setState({
          galleryImages,
          basketItems: items,
          orderAmount: orderAmount.toFixed(2),
          deliveryAmount: deliveryAmount.toFixed(2),
          specialOffersSaving: specialOffersSaving.toFixed(2),
          totalWithoutSpecialOffers: totalWithoutSpecialOffers.toFixed(2),
          voucher,
          voucherSaving: voucherSaving.toFixed(2),
          isVoucherEnterButtonClicked: true,
          isAutoVoucherModalOpen: Boolean(voucher?.voucherId && voucher.autoApply),
          autoVoucherMessage:
            voucher?.voucherId && voucher.autoApply
              ? `Voucher "${voucher.code}" was automatically applied! Your savings: ${formattedVoucherSaving}.`
              : '',
        });
      });
  };

  onVoucherCancelClick = () => {
    this.setState({
      isVoucherModalOpen: false,
    });
  };

  onVoucherOkClick = () => {
    this.setState({
      isVoucherModalOpen: false,
      isVoucherEnterButtonClicked: false,
      voucherText: '',
    });
  };

  private renderAutoVoucherModal() {
    if (!this.state.isAutoVoucherModalOpen) return null;
    return (
      <SimpleModal isOpen={true}>
        <div className="p-4">
          <h4>Voucher applied!</h4>
          <p>{this.state.autoVoucherMessage}</p>
          <Button onClick={() => this.setState({ isAutoVoucherModalOpen: false })} text="OK" />
        </div>
      </SimpleModal>
    );
  }

  render() {
    const {
      basketItems,
      orderAmount,
      deliveryAmount,
      voucherSaving,
      specialOffersSaving,
      totalWithoutSpecialOffers,
    } = this.state;
    const total = (orderAmount - deliveryAmount).toFixed(2);
    const isBasketItemsExist = basketItems.length !== 0;
    const isSpecialOffersSavingExist = specialOffersSaving > 0;

    const discountType = this.state.voucher?.discount?.discountType;
    const isVoucherSavingExist =
      this.state.voucherSaving > 0 || discountType === 'FREE_POSTAGE' || discountType === 'REDUCED_POSTAGE';

    return (
      <div className="bBasketWrapper">
        {/*Place for render modal windows*/}
        {this.renderAutoVoucherModal()}
        {this.renderVoucherModal()}
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-xl-12">
              <div className="bBasket">
                <div className="eBasketTitle">YOUR BASKET</div>
                {!isBasketItemsExist && <div className="eBasketText">Your basket is empty</div>}
                <div className="row justify-content-center">
                  <div className="eBasketItems">
                    {isBasketItemsExist &&
                      basketItems.map((item, index) => (
                        <div className="eBasketItem" key={item.id}>
                          <div className="eBasketImageWrapper">
                            <img
                              className="eBasketItemImage"
                              src={`${window.apiFile}/storage/images/${item.ticketId}`}
                            />
                          </div>
                          <div className="eBasketPhotoInfo">
                            <div className="eBasketItemText">Class name: {item.classname}</div>
                            <div className="eBasketItemText">Photo year: {this.getYear(item.jobDate)}</div>
                            <div className="eBasketItemText">Product: {item.productName}</div>
                          </div>
                          <div className="eBasketPhotoQuantityPrice">
                            <div className="eBasketItemText">Price</div>
                            <div className="eBasketItemText">{`${CURRENCY_SYMBOL.POUND}${(
                              item.productPriceSetPrice * item.quantity
                            ).toFixed(2)}`}</div>
                            <div className="eBasketItemText">Quantity</div>
                            <div className="eBasketItemQuantity">
                              <div className="eBasketItemQuantityChange" onClick={() => this.onMinusClick(item, index)}>
                                -
                              </div>
                              <div className="eBasketItemText">{item.quantity}</div>
                              <div className="eBasketItemQuantityChange" onClick={() => this.onPlusClick(item, index)}>
                                +
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                  <div className="eBasketFooter">
                    <div className="eBasketFooterLeft">
                      {isBasketItemsExist && (
                        <Button onClick={this.onVoucherClick} text="Voucher" customClass="eBasketFooterButton" />
                      )}
                      <button className="eBasketFooterButton" onClick={this.onGalleryButtonClick}>
                        Gallery
                      </button>
                    </div>
                    <div className="eBasketFooterRight">
                      {(isSpecialOffersSavingExist || isVoucherSavingExist) && (
                        <div className="eBasketFooterText mTextBold">{`Subtotal: ${CURRENCY_SYMBOL.POUND}${totalWithoutSpecialOffers}`}</div>
                      )}
                      {isSpecialOffersSavingExist && (
                        <div className="eBasketFooterText mTextBold">{`Special offer saving: ${CURRENCY_SYMBOL.POUND}${specialOffersSaving}`}</div>
                      )}
                      {isVoucherSavingExist && (
                        <div className="eBasketFooterText mTextBold">
                          {this.formatVoucherSaving(Number(this.state.voucherSaving), this.state.voucher)}
                        </div>
                      )}
                      <div className="eBasketFooterText mTextBold mTextPrimary">{`Total: ${CURRENCY_SYMBOL.POUND}${total}`}</div>
                      <div className="eBasketFooterActions">
                        <button className="eBasketFooterButton clearBasketButton" onClick={this.onClearBasketClick}>
                          Clear Basket
                        </button>
                        <button
                          className="eBasketFooterButton"
                          onClick={this.onCheckoutButtonClick}
                          disabled={!isBasketItemsExist}
                        >
                          Checkout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
